import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { bankReconciliationService } from 'api/services';
import type { ReconciliationStartDto, OpeningItemType, SaveReconciliationStartRequest } from 'api/types';

// ============================================================================
// RECONCILIATION START (ADR-0055 §7) — set once per bank account: the date
// reconciliation begins after, the bank statement's balance on that date, and
// the items then outstanding (cheques issued not presented, deposits not yet
// credited). Book entries on or before the date count as cleared; the opening
// items are matched to later statement lines like book entries. Any date works —
// not only the day the books begin.
// ============================================================================

const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.5rem' };
const td: React.CSSProperties = { padding: '0.3rem 0.4rem', verticalAlign: 'top' };
const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const cellInput: React.CSSProperties = { fontSize: '0.8rem', padding: '0.25rem 0.4rem', width: '100%' };
const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const money = (n?: number | null) => (n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '');
const isoDate = (s?: string | null) => (s ? s.slice(0, 10) : '');

const TYPE_LABEL: Record<OpeningItemType, string> = {
  ISSUED_NOT_PRESENTED: 'Issued, not presented',
  DEPOSITED_NOT_CREDITED: 'Deposited, not credited',
};

interface ItemRow {
  key: string;
  id: string | null;
  itemType: OpeningItemType;
  itemDate: string;
  reference: string;
  description: string;
  amount: string;
  /** Matched to a statement line: type, date and amount are fixed. */
  matched: boolean;
  clearedDate?: string | null;
}

let seq = 0;
const newKey = () => `row-${++seq}`;

export interface ReconciliationStartModalProps {
  isOpen: boolean;
  bankLedgerId: string;
  bankLedgerName?: string | null;
  canUpdate: boolean;
  canDelete: boolean;
  onClose: () => void;
  /** After a save or removal — the screen reloads its statement. */
  onSaved: (start: ReconciliationStartDto | null) => void;
}

export const ReconciliationStartModal: React.FC<ReconciliationStartModalProps> = ({
  isOpen, bankLedgerId, bankLedgerName, canUpdate, canDelete, onClose, onSaved,
}) => {
  const [start, setStart] = useState<ReconciliationStartDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [bankBalance, setBankBalance] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemRow[]>([]);

  const fill = useCallback((s: ReconciliationStartDto | null) => {
    setStart(s);
    setStartDate(isoDate(s?.startDate));
    setBankBalance(s ? String(s.bankBalance) : '');
    setNotes(s?.notes ?? '');
    setItems((s?.openingItems ?? []).map(i => ({
      key: newKey(), id: i.id, itemType: i.itemType, itemDate: isoDate(i.itemDate),
      reference: i.reference ?? '', description: i.description ?? '', amount: String(i.amount),
      matched: !!i.matchId, clearedDate: i.clearedDate,
    })));
  }, []);

  useEffect(() => {
    if (!isOpen || !bankLedgerId) return;
    let live = true;
    setLoading(true);
    bankReconciliationService.getStart(bankLedgerId)
      .then(s => { if (live) fill(s); })
      .catch(err => notificationService.toast.error(err?.response?.data?.message || 'Could not load the reconciliation start.'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [isOpen, bankLedgerId, fill]);

  const upd = (key: string, patch: Partial<ItemRow>) => setItems(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = (itemType: OpeningItemType) => setItems(prev => [...prev, {
    key: newKey(), id: null, itemType, itemDate: startDate, reference: '', description: '', amount: '', matched: false,
  }]);

  const totals = useMemo(() => ({
    inp: items.filter(i => i.itemType === 'ISSUED_NOT_PRESENTED').reduce((s, i) => s + num(i.amount), 0),
    dnc: items.filter(i => i.itemType === 'DEPOSITED_NOT_CREDITED').reduce((s, i) => s + num(i.amount), 0),
  }), [items]);

  // The book balance is the server's figure at the SAVED start date; while the date is being changed it is stale.
  const dateChanged = !!start && isoDate(start.startDate) !== startDate;
  const expected = start && !dateChanged ? start.bookBalance - totals.dnc + totals.inp : null;
  const difference = expected == null || bankBalance.trim() === '' ? null : num(bankBalance) - expected;
  const anyMatched = items.some(i => i.matched);
  const readOnly = !canUpdate;

  const handleSave = async () => {
    if (!startDate) { notificationService.toast.warning('Enter the start date.'); return; }
    if (bankBalance.trim() === '') { notificationService.toast.warning('Enter the bank balance on the start date.'); return; }
    const bad = items.findIndex(i => !i.itemDate || num(i.amount) <= 0);
    if (bad >= 0) { notificationService.toast.warning(`Opening item ${bad + 1}: enter its date and an amount above zero.`); return; }
    const req: SaveReconciliationStartRequest = {
      startDate, bankBalance: num(bankBalance), notes: notes.trim() || null, rowVersion: start?.rowVersion ?? null,
      openingItems: items.map(i => ({
        id: i.id, itemType: i.itemType, itemDate: i.itemDate,
        reference: i.reference.trim() || null, description: i.description.trim() || null, amount: num(i.amount),
      })),
    };
    setSaving(true);
    try {
      const saved = await bankReconciliationService.saveStart(bankLedgerId, req);
      fill(saved);
      notificationService.toast.success(Math.abs(saved.difference) < 0.005
        ? 'Reconciliation start saved — it agrees with the books.'
        : `Reconciliation start saved — out by ${money(saved.difference)}; see the check below.`);
      onSaved(saved);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the reconciliation start.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await bankReconciliationService.deleteStart(bankLedgerId);
      setConfirmRemove(false);
      fill(null);
      notificationService.toast.success('Reconciliation start removed.');
      onSaved(null);
    } catch (err: any) {
      setConfirmRemove(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not remove the reconciliation start.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const checkRow = (label: string, value: React.ReactNode, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontWeight: strong ? 700 : 400, padding: '0.15rem 0' }}>
      <span>{label}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Reconciliation Start — {bankLedgerName || 'Bank account'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ ...muted, fontSize: '0.78rem', marginBottom: '0.75rem' }}>
            Set once per bank account. Reconciliation begins after the start date: entries in the books on or before it count as cleared,
            and the items then outstanding — cheques issued but not presented, deposits not yet credited — are matched to later statement
            lines like any book entry. Pick any date with a known bank balance; it need not be the day the books begin.
            {loading && <span> Loading…</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '180px 200px 1fr', gap: '0.75rem', marginBottom: '0.9rem' }}>
            <div>
              <label style={labelStyle}>Start date</label>
              <input type="date" className="form-input" style={inputStyle} value={startDate} disabled={readOnly || saving || anyMatched}
                onChange={e => setStartDate(e.target.value)} />
              {anyMatched && <div style={{ ...muted, fontSize: '0.7rem', marginTop: '0.2rem' }}>Fixed while items are matched</div>}
            </div>
            <div>
              <label style={labelStyle}>Bank balance on that date</label>
              <input type="number" inputMode="decimal" className="form-input" style={{ ...inputStyle, textAlign: 'right' }}
                value={bankBalance} disabled={readOnly || saving} placeholder="As per the statement"
                onChange={e => setBankBalance(e.target.value)} />
              <div style={{ ...muted, fontSize: '0.7rem', marginTop: '0.2rem' }}>Money in the account; negative if overdrawn</div>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input type="text" className="form-input" style={inputStyle} value={notes} maxLength={500} disabled={readOnly || saving}
                placeholder="e.g. balance from the March statement, page 4" onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <strong style={{ fontSize: '0.85rem' }}>Items outstanding on the start date</strong>
            {!readOnly && (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }} onClick={() => addRow('ISSUED_NOT_PRESENTED')} disabled={saving}>
                  <IconSystem.add size={12} /> Cheque / payment not presented
                </button>
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }} onClick={() => addRow('DEPOSITED_NOT_CREDITED')} disabled={saving}>
                  <IconSystem.add size={12} /> Deposit not credited
                </button>
              </div>
            )}
          </div>
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto', maxHeight: '36vh' }}>
            <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                  <th style={{ ...th, width: 190 }}>Type</th>
                  <th style={{ ...th, width: 140 }}>Date</th>
                  <th style={{ ...th, width: 150 }}>Cheque / reference</th>
                  <th style={th}>Payee / payer, note</th>
                  <th style={{ ...th, width: 140, textAlign: 'right' }}>Amount</th>
                  <th style={{ ...th, width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, ...muted, padding: '0.9rem', textAlign: 'center' }}>
                    Nothing outstanding — the bank balance equals the book balance on the start date.
                  </td></tr>
                )}
                {items.map(r => {
                  const fixed = readOnly || saving || r.matched;
                  return (
                    <tr key={r.key} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={td}>
                        <select className="form-input" style={cellInput} value={r.itemType} disabled={fixed}
                          onChange={e => upd(r.key, { itemType: e.target.value as OpeningItemType })}>
                          {(Object.keys(TYPE_LABEL) as OpeningItemType[]).map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <input type="date" className="form-input" style={cellInput} value={r.itemDate} disabled={fixed}
                          onChange={e => upd(r.key, { itemDate: e.target.value })} />
                      </td>
                      <td style={td}>
                        <input type="text" className="form-input" style={cellInput} value={r.reference} maxLength={100} disabled={readOnly || saving}
                          placeholder="Cheque no. / UTR" onChange={e => upd(r.key, { reference: e.target.value })} />
                      </td>
                      <td style={td}>
                        <input type="text" className="form-input" style={cellInput} value={r.description} maxLength={300} disabled={readOnly || saving}
                          onChange={e => upd(r.key, { description: e.target.value })} />
                      </td>
                      <td style={td}>
                        <input type="number" inputMode="decimal" className="form-input" style={{ ...cellInput, textAlign: 'right' }} value={r.amount} disabled={fixed}
                          onChange={e => upd(r.key, { amount: e.target.value })} />
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {r.matched
                          ? <span className="badge" style={{ background: '#059669', color: '#fff' }} title="Matched to a statement line — unmatch it there to change or remove it">
                              Cleared {fmtDate(r.clearedDate)}
                            </span>
                          : !readOnly && (
                            <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }} disabled={saving}
                              onClick={() => setItems(prev => prev.filter(x => x.key !== r.key))} title="Remove">
                              <IconSystem.trash size={12} />
                            </button>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '0.9rem', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ minWidth: 380, border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.6rem 0.8rem', fontSize: '0.8125rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Check against the books</div>
              {!start || dateChanged ? (
                <div style={muted}>Save to see the book balance on {startDate ? fmtDate(startDate) : 'the start date'} and whether the start agrees with it.</div>
              ) : (
                <>
                  {checkRow(`Balance in the books on ${fmtDate(start.startDate)}`, money(start.bookBalance))}
                  {checkRow('Less: deposits not credited', money(totals.dnc))}
                  {checkRow('Add: cheques / payments not presented', money(totals.inp))}
                  {checkRow('Expected bank balance', money(expected), true)}
                  {checkRow('Bank balance entered', money(bankBalance.trim() === '' ? null : num(bankBalance)))}
                  <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '0.3rem', paddingTop: '0.3rem',
                    color: difference != null && Math.abs(difference) < 0.005 ? '#059669' : '#dc2626' }}>
                    {checkRow('Difference', difference == null ? '—' : money(difference), true)}
                  </div>
                  {difference != null && Math.abs(difference) >= 0.005 && (
                    <div style={{ ...muted, fontSize: '0.72rem', marginTop: '0.2rem' }}>
                      An item outstanding on the start date is missing or mis-typed, or the balance is not the statement's. It can be saved anyway; the reconciliation statement flags it.
                    </div>
                  )}
                </>
              )}
              {start?.updatedByName && (
                <div style={{ ...muted, fontSize: '0.7rem', marginTop: '0.4rem' }}>
                  Last saved {start.updatedAt ? new Date(start.updatedAt).toLocaleString() : ''} by {start.updatedByName}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {start && canDelete && (
            <button className="btn btn-secondary" style={{ marginRight: 'auto', color: '#dc2626' }} onClick={() => setConfirmRemove(true)} disabled={saving || anyMatched}
              title={anyMatched ? 'Unmatch the cleared opening items first' : 'Remove the start'}>
              Remove start
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
          {canUpdate && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving…' : 'Save'}</button>
          )}
        </div>
      </div>
      <ConfirmDialog open={confirmRemove} title="Remove Reconciliation Start"
        message="Remove the start and its opening items? Until a new one is set, every bank entry since the books began counts as outstanding in the reconciliation statement."
        confirmLabel="Remove" cancelLabel="Keep" variant="danger" loading={saving} loadingMessage="Removing…"
        onConfirm={handleRemove} onCancel={() => setConfirmRemove(false)} />
    </>
  );
};

export default ReconciliationStartModal;
